import React, { useEffect, useMemo } from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm, useWatch, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import useOrganizationUsers from 'views/App/Portal/LexID/Users/_data_sources/useOrganizationUsers';
import type { User } from 'views/App/Portal/LexID/Users/_types/user';
import UserPicker from 'components/_pickers/UserPicker';
import { translate as t } from 'hooks/useTranslations';
import { neutral } from '_lex/_foundations/phxColors';

import type { DurationUnit, WorkflowMiddleStep, WorkflowRejectAction } from '../../_types/workflowBuilder';

const DURATION_UNITS: DurationUnit[] = ['days', 'hours', 'weeks'];
const REJECT_ACTIONS: WorkflowRejectAction[] = ['returnToAuthor', 'returnToPreviousStep'];
const NUMBER_CHOICES = Array.from({ length: 50 }, (_, i) => i + 1);

const staticFormLabelSx = {
  display: 'block',
  position: 'static',
  transform: 'none',
  mb: 2,
  fontWeight: 600,
  fontSize: '0.875rem',
  color: 'text.primary',
} as const;

function InfoFormHelperText({ children }: { children: React.ReactNode }) {
  return (
    <FormHelperText
      component='div'
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        mx: 0,
        color: neutral[500],
      }}
    >
      <InfoOutlinedIcon
        sx={{ fontSize: 16, color: neutral[400], flexShrink: 0, mt: '2px' }}
        aria-hidden
      />
      <Box
        component='span'
        sx={{ lineHeight: 1.43 }}
      >
        {children}
      </Box>
    </FormHelperText>
  );
}

export interface EditWorkflowStepFormProps {
  step: WorkflowMiddleStep;
  organizationId: string;
  onStepChange: (patch: Partial<WorkflowMiddleStep>) => void;
}

type FormShape = {
  name: string;
  reviewerUserIds: string[];
  timeAllowedValue: number | '';
  timeAllowedUnit: DurationUnit | '';
  rejectAction: WorkflowRejectAction | '';
  timeToResubmitValue: number | '';
  timeToResubmitUnit: DurationUnit | '';
};

function buildSchema() {
  return yup.object({
    name: yup.string().required('Required'),
    reviewerUserIds: yup.array().of(yup.string().required()).optional(),
    timeAllowedValue: yup.number().typeError('Required').min(1).max(50).integer().required('Required'),
    timeAllowedUnit: yup.mixed<DurationUnit>().oneOf(DURATION_UNITS, 'Required').required('Required'),
    rejectAction: yup.mixed<WorkflowRejectAction>().oneOf(REJECT_ACTIONS, 'Required').required('Required'),
    timeToResubmitValue: yup.number().when('rejectAction', {
      is: (val: string) => !!val,
      then: (s) => s.typeError('Required').min(1).max(50).integer().required('Required'),
      otherwise: (s) => s.optional().nullable(),
    }),
    timeToResubmitUnit: yup.mixed<DurationUnit>().when('rejectAction', {
      is: (val: string) => !!val,
      then: (s) => s.oneOf(DURATION_UNITS, 'Required').required('Required'),
      otherwise: (s) => s.optional().nullable(),
    }),
  });
}

function stepToFormValues(step: WorkflowMiddleStep): FormShape {
  return {
    name: step.name,
    reviewerUserIds: step.reviewerUserIds,
    timeAllowedValue: step.timeAllowed?.value ?? '',
    timeAllowedUnit: step.timeAllowed?.unit ?? '',
    rejectAction: step.rejectAction ?? '',
    timeToResubmitValue: step.timeToResubmit?.value ?? '',
    timeToResubmitUnit: step.timeToResubmit?.unit ?? '',
  };
}

function usersForPicker(ids: string[], usersById: Map<string, User>): User[] {
  return ids.map((id) => usersById.get(id)).filter((u): u is User => !!u);
}

const timeRowStackSx = { width: '100%', alignItems: 'stretch' as const };

const EditWorkflowStepForm: React.FC<EditWorkflowStepFormProps> = ({ step, organizationId, onStepChange }) => {
  const usersQuery = useOrganizationUsers<User>(organizationId, { size: 100 });

  useEffect(() => {
    if (usersQuery.hasNextPage) {
      void usersQuery.fetchNextPage();
    }
  }, [usersQuery.data, usersQuery.hasNextPage, usersQuery.fetchNextPage]);

  const allUsers = useMemo(() => usersQuery.data?.pages.flatMap((p) => p.content) ?? [], [usersQuery.data]);

  const usersById = useMemo(() => {
    const m = new Map<string, User>();
    for (const u of allUsers) {
      if (u.id) m.set(u.id, u);
    }
    return m;
  }, [allUsers]);

  const schema = useMemo(() => buildSchema(), []);

  const {
    control,
    getValues,
    setValue,
    trigger,
    formState: { errors },
  } = useForm<FormShape>({
    resolver: yupResolver(schema) as Resolver<FormShape>,
    mode: 'all',
    defaultValues: stepToFormValues(step),
  });

  useEffect(() => {
    void trigger();
  }, [trigger]);

  const rejectAction = useWatch({ control, name: 'rejectAction' });
  const showTimeToResubmit = !!rejectAction;

  return (
    <Stack
      spacing={0}
      component='form'
      noValidate
      sx={{
        // Global theme sets MuiFormControl paddingTop: 2rem (react-select / floating labels). This form uses external FormLabels instead.
        '& .MuiFormControl-root': { paddingTop: 0 },
      }}
    >
      <Box
        sx={{
          bgcolor: neutral[50],
          py: 1.5,
          px: 2,
          mx: -2,
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{
            fontWeight: 600,
            p: 3,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {t('performanceReporting.reviewWorkflows.new.editStep.stepDetailsSection')}
        </Typography>
      </Box>

      <Stack
        spacing={2}
        sx={{ px: 2, pt: 4 }}
      >
        <Controller
          name='name'
          control={control}
          render={({ field }) => {
            const stepNameFieldId = `edit-step-name-${step.localKey}`;
            const stepNameLabelId = `${stepNameFieldId}-label`;
            return (
              <Box>
                <FormLabel
                  id={stepNameLabelId}
                  htmlFor={stepNameFieldId}
                  required
                  error={!!errors.name}
                  sx={staticFormLabelSx}
                >
                  {t('performanceReporting.reviewWorkflows.new.editStep.stepName')}
                </FormLabel>
                <TextField
                  {...field}
                  id={stepNameFieldId}
                  slotProps={{ htmlInput: { 'aria-labelledby': stepNameLabelId } }}
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  onChange={(e) => {
                    field.onChange(e);
                    onStepChange({ name: e.target.value });
                  }}
                />
              </Box>
            );
          }}
        />

        <Controller
          name='reviewerUserIds'
          control={control}
          render={({ field }) => (
            <Box>
              <FormLabel
                required
                error={!!errors.reviewerUserIds}
                sx={staticFormLabelSx}
              >
                {t('performanceReporting.reviewWorkflows.new.editStep.reviewers')}
              </FormLabel>
              <UserPicker
                // TODO: remove `disabled` once the reviewers list is supported by the backend
                disabled
                key={`${step.localKey}-${field.value.join('|')}`}
                id={`edit-step-reviewers-${step.localKey}`}
                allowMultiple
                organizationId={organizationId}
                placeholder={t('performanceReporting.reviewWorkflows.new.editStep.reviewersPlaceholder')}
                value={usersForPicker(field.value, usersById)}
                onChange={(users) => {
                  const ids = users.map((u) => u.id!).filter(Boolean);
                  field.onChange(ids);
                  onStepChange({ reviewerUserIds: ids });
                }}
                error={errors.reviewerUserIds}
              />
              {!errors.reviewerUserIds && (
                <InfoFormHelperText>
                  {t('performanceReporting.reviewWorkflows.new.editStep.reviewersHelper')}
                </InfoFormHelperText>
              )}
            </Box>
          )}
        />

        <Box>
          <FormLabel
            required
            error={!!errors.timeAllowedValue || !!errors.timeAllowedUnit}
            sx={staticFormLabelSx}
          >
            {t('performanceReporting.reviewWorkflows.new.editStep.timeAllowed')}
          </FormLabel>
          <Stack
            direction='row'
            spacing={2}
            sx={timeRowStackSx}
          >
            <Controller
              name='timeAllowedValue'
              control={control}
              render={({ field }) => (
                <FormControl
                  sx={{ flexShrink: 0, minWidth: 100 }}
                  error={!!errors.timeAllowedValue}
                >
                  <Select
                    {...field}
                    displayEmpty
                    onChange={(e) => {
                      field.onChange(e);
                      const raw = e.target.value;
                      const value = raw === '' ? undefined : Number(raw);
                      const unit = getValues('timeAllowedUnit') || undefined;
                      onStepChange({
                        timeAllowed: value !== undefined && unit
                          ? { value, unit: unit as DurationUnit }
                          : undefined,
                      });
                    }}
                  >
                    <MenuItem value=''><em>--</em></MenuItem>
                    {NUMBER_CHOICES.map((n) => (
                      <MenuItem key={n} value={n}>{n}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <Controller
              name='timeAllowedUnit'
              control={control}
              render={({ field }) => (
                <FormControl
                  sx={{ flex: 1, minWidth: 0 }}
                  error={!!errors.timeAllowedUnit}
                >
                  <Select
                    {...field}
                    displayEmpty
                    onChange={(e) => {
                      field.onChange(e);
                      const unit = e.target.value as DurationUnit | '';
                      const valueRaw = getValues('timeAllowedValue');
                      const value = valueRaw === '' || valueRaw === undefined ? undefined : Number(valueRaw);
                      onStepChange({
                        timeAllowed: value !== undefined && unit
                          ? { value, unit: unit as DurationUnit }
                          : undefined,
                      });
                    }}
                  >
                    <MenuItem value=''><em>{t('performanceReporting.reviewWorkflows.new.editStep.unitPlaceholder')}</em></MenuItem>
                    {DURATION_UNITS.map((u) => (
                      <MenuItem key={u} value={u}>
                        {t(`performanceReporting.reviewWorkflows.new.editStep.durationUnit.${u}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Stack>
        </Box>
      </Stack>

      <Box
        sx={{
          bgcolor: neutral[50],
          py: 1.5,
          mt: 3,
          px: 2,
          mx: -2,
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{
            fontWeight: 600,
            letterSpacing: '0.08em',
            p: 3,
            textTransform: 'uppercase',
            color: 'text.secondary',
          }}
        >
          {t('performanceReporting.reviewWorkflows.new.editStep.rejectionSection')}
        </Typography>
      </Box>

      <Stack
        spacing={4}
        sx={{ px: 2, pt: 4, pb: 2 }}
      >
        <Controller
          name='rejectAction'
          control={control}
          render={({ field }) => {
            const rejectFieldId = `edit-step-reject-action-${step.localKey}`;
            const rejectLabelId = `${rejectFieldId}-label`;
            return (
              <Box>
                <FormLabel
                  id={rejectLabelId}
                  htmlFor={rejectFieldId}
                  required
                  error={!!errors.rejectAction}
                  sx={staticFormLabelSx}
                >
                  {t('performanceReporting.reviewWorkflows.new.editStep.ifReviewerRejects')}
                </FormLabel>
                <FormControl
                  fullWidth
                  error={!!errors.rejectAction}
                >
                  <Select
                    {...field}
                    id={rejectFieldId}
                    slotProps={{ input: { 'aria-labelledby': rejectLabelId } }}
                    displayEmpty
                    onChange={(e) => {
                      field.onChange(e);
                      const value = e.target.value as WorkflowRejectAction | '';
                      setValue('timeToResubmitValue', '');
                      setValue('timeToResubmitUnit', '');
                      onStepChange({
                        rejectAction: value || undefined,
                        timeToResubmit: undefined,
                      });
                    }}
                  >
                    <MenuItem value=''><em>{t('performanceReporting.reviewWorkflows.new.editStep.rejectActionPlaceholder')}</em></MenuItem>
                    {REJECT_ACTIONS.map((a) => (
                      <MenuItem key={a} value={a}>
                        {t(`performanceReporting.reviewWorkflows.new.editStep.rejectAction.${a}`)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            );
          }}
        />

        {showTimeToResubmit && (
          <Box>
            <FormLabel
              required
              error={!!errors.timeToResubmitValue || !!errors.timeToResubmitUnit}
              sx={staticFormLabelSx}
            >
              {t('performanceReporting.reviewWorkflows.new.editStep.timeToResubmit')}
            </FormLabel>
            <Stack
              direction='row'
              spacing={2}
              sx={timeRowStackSx}
            >
              <Controller
                name='timeToResubmitValue'
                control={control}
                render={({ field }) => (
                  <FormControl
                    sx={{ flexShrink: 0, minWidth: 100 }}
                    error={!!errors.timeToResubmitValue}
                  >
                    <Select
                      {...field}
                      displayEmpty
                      onChange={(e) => {
                        field.onChange(e);
                        const raw = e.target.value;
                        const value = raw === '' ? undefined : Number(raw);
                        const unit = getValues('timeToResubmitUnit') || undefined;
                        onStepChange({
                          timeToResubmit: value !== undefined && unit
                            ? { value, unit: unit as DurationUnit }
                            : undefined,
                        });
                      }}
                    >
                      <MenuItem value=''><em>--</em></MenuItem>
                      {NUMBER_CHOICES.map((n) => (
                        <MenuItem key={n} value={n}>{n}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
              <Controller
                name='timeToResubmitUnit'
                control={control}
                render={({ field }) => (
                  <FormControl
                    sx={{ flex: 1, minWidth: 0 }}
                    error={!!errors.timeToResubmitUnit}
                  >
                    <Select
                      {...field}
                      displayEmpty
                      onChange={(e) => {
                        field.onChange(e);
                        const unit = e.target.value as DurationUnit | '';
                        const valueRaw = getValues('timeToResubmitValue');
                        const value = valueRaw === '' || valueRaw === undefined ? undefined : Number(valueRaw);
                        onStepChange({
                          timeToResubmit: value !== undefined && unit
                            ? { value, unit: unit as DurationUnit }
                            : undefined,
                        });
                      }}
                    >
                      <MenuItem value=''><em>{t('performanceReporting.reviewWorkflows.new.editStep.unitPlaceholder')}</em></MenuItem>
                      {DURATION_UNITS.map((u) => (
                        <MenuItem key={u} value={u}>
                          {t(`performanceReporting.reviewWorkflows.new.editStep.durationUnit.${u}`)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              />
            </Stack>
            <InfoFormHelperText>
              {t('performanceReporting.reviewWorkflows.new.editStep.timeToResubmitHelper')}
            </InfoFormHelperText>
          </Box>
        )}
      </Stack>
    </Stack>
  );
};

export default EditWorkflowStepForm;
